import React, { useState, useEffect } from "react";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_API_URL}/users`

// Reusable icon components (matching the style of your Home page)
const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M17 3l4 4-7 7H10v-4l7-7z" />
    <path d="M4 20h16" />
  </svg>
);

const DeleteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState("add"); // "add" or "edit"
  const [currentUserId, setCurrentUserId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    username: "",
    email: "",
    phone: "",
    password: "",
  });

  // Fetch all users
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/users`);
      setUsers(response.data.users || []);
      setError(null);
    } catch (err) {
      console.error(err);
      setError("Failed to load users. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Open add form
  const openAddForm = () => {
    setFormMode("add");
    setCurrentUserId(null);
    setFormData({ name: "", username: "", email: "", phone: "", password: "" });
    setShowForm(true);
  };

  // Open edit form with existing data
  const openEditForm = (user) => {
    setFormMode("edit");
    setCurrentUserId(user._id);
    setFormData({
      name: user.name,
      username: user.username,
      email: user.email,
      phone: user.phone,
      password: "", // password field is optional for update
    });
    setShowForm(true);
  };

  // Submit add / edit
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formMode === "add") {
        // POST /register
        await axios.post(`${API_BASE}/register`, formData);
      } else {
        // PUT /update-user/:id
        const { password, ...updateData } = formData; // exclude password if empty
        await axios.put(`${API_BASE}/update-user/${currentUserId}`, updateData);
      }
      setShowForm(false);
      fetchUsers(); // refresh list
    } catch (err) {
      console.error(err);
      alert(`Error ${formMode === "add" ? "creating" : "updating"} user.`);
    }
  };

  // Delete user
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${API_BASE}/delete-user/${id}`);
      fetchUsers();
    } catch (err) {
      console.error(err);
      alert("Error deleting user.");
    }
  };

  return (
    <div
      style={{
        padding: "24px 20px",
        backgroundColor: "#0e0e0e",
        color: "#e8e0cc",
        fontFamily: "'DM Sans', 'Helvetica Neue', Arial, sans-serif",
        minHeight: "100vh",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');
        .users-table-container {
          overflow-x: auto;
          margin-top: 24px;
        }
        .users-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }
        .users-table th {
          text-align: left;
          padding: 16px 12px;
          color: #8a7a50;
          font-weight: 600;
          letter-spacing: 0.08em;
          border-bottom: 1px solid #2a2618;
        }
        .users-table td {
          padding: 14px 12px;
          border-bottom: 1px solid #1e1e1e;
          vertical-align: middle;
        }
        .users-table tr:hover td {
          background-color: #141410;
        }
        .action-buttons {
          display: flex;
          gap: 12px;
        }
        .action-btn {
          background: none;
          border: none;
          color: #8a7a50;
          cursor: pointer;
          padding: 6px;
          border-radius: 4px;
          transition: all 0.15s;
          display: inline-flex;
          align-items: center;
        }
        .action-btn:hover {
          color: #d4a017;
          background: #1e1c10;
        }
        .user-avatar {
          width: 32px;
          height: 32px;
          background: #1e1c10;
          border-radius: 40px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #d4a017;
          font-weight: 600;
          font-size: 14px;
          border: 1px solid #2e2a10;
        }
        /* Card view for mobile */
        @media (max-width: 760px) {
          .users-table, .users-table thead, .users-table tbody, .users-table tr, .users-table th, .users-table td {
            display: block;
          }
          .users-table thead {
            display: none;
          }
          .users-table tr {
            margin-bottom: 20px;
            border: 1px solid #2a2618;
            border-radius: 14px;
            background: #161616;
            padding: 16px;
          }
          .users-table td {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px 0;
            border-bottom: 1px solid #2a2618;
          }
          .users-table td:last-child {
            border-bottom: none;
          }
          .users-table td::before {
            content: attr(data-label);
            font-weight: 600;
            color: #8a7a50;
            letter-spacing: 0.08em;
            font-size: 11px;
            width: 100px;
          }
          .action-buttons {
            justify-content: flex-end;
          }
        }
        .form-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.7);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .form-card {
          background: #161616;
          border: 1px solid #2a2618;
          border-radius: 20px;
          width: 90%;
          max-width: 500px;
          padding: 28px;
          box-shadow: 0 20px 35px rgba(0,0,0,0.5);
        }
        .form-group {
          margin-bottom: 18px;
        }
        .form-group label {
          display: block;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.1em;
          color: #8a7a50;
          margin-bottom: 6px;
        }
        .form-group input {
          width: 100%;
          background: #0e0e0e;
          border: 1px solid #2a2618;
          border-radius: 8px;
          padding: 10px 12px;
          color: #e8e0cc;
          font-family: inherit;
          font-size: 13px;
          outline: none;
        }
        .form-group input:focus {
          border-color: #d4a017;
        }
        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 24px;
        }
        .error-message {
          background: #2a1a1a;
          border-left: 3px solid #e5484d;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 20px;
          font-size: 13px;
        }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px", marginBottom: "24px" }}>
        <div>
          <h1 style={{ fontSize: "clamp(20px, 4vw, 28px)", fontWeight: 400, letterSpacing: "0.02em", margin: 0 }}>
            User Management
          </h1>
          <p style={{ color: "#7a6a50", fontSize: "13px", marginTop: "6px" }}>Manage agency members and their access</p>
        </div>
        <button className="ap-btn-gold" onClick={openAddForm} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <PlusIcon /> ADD USER
        </button>
      </div>

      {/* Loading & Error */}
      {loading && <div style={{ textAlign: "center", padding: "40px", color: "#8a7a50" }}>Loading users...</div>}
      {error && <div className="error-message">{error}</div>}

      {/* Users Table / Cards */}
      {!loading && !error && (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Username</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", padding: "40px", color: "#5a5040" }}>No users found. Click "ADD USER" to create one.</td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user._id}>
                    <td data-label="User">
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <div className="user-avatar">{user.name.charAt(0).toUpperCase()}</div>
                        <span>{user.name}</span>
                      </div>
                    </td>
                    <td data-label="Username">{user.username}</td>
                    <td data-label="Email">{user.email}</td>
                    <td data-label="Phone">{user.phone}</td>
                    <td data-label="Created">{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td data-label="Actions">
                      <div className="action-buttons">
                        <button className="action-btn" onClick={() => openEditForm(user)}><EditIcon /></button>
                        <button className="action-btn" onClick={() => handleDelete(user._id)}><DeleteIcon /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal Form */}
      {showForm && (
        <div className="form-overlay" onClick={() => setShowForm(false)}>
          <div className="form-card" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2 style={{ fontSize: "20px", fontWeight: 500, margin: 0 }}>{formMode === "add" ? "Add New User" : "Edit User"}</h2>
              <button onClick={() => setShowForm(false)} style={{ background: "none", border: "none", color: "#8a7a50", cursor: "pointer" }}>
                <CloseIcon />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Full Name *</label>
                <input name="name" value={formData.name} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Username *</label>
                <input name="username" value={formData.username} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Phone *</label>
                <input name="phone" value={formData.phone} onChange={handleInputChange} required />
              </div>
              {formMode === "add" && (
                <div className="form-group">
                  <label>Password *</label>
                  <input type="password" name="password" value={formData.password} onChange={handleInputChange} required />
                </div>
              )}
              <div className="form-actions">
                <button type="button" className="ap-btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="ap-btn-gold">{formMode === "add" ? "Create User" : "Save Changes"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}